import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { US_TIMEZONE_REGIONS } from "@/shared/lib/usTimezones";
import { cn } from "@/shared/lib/utils";

interface TimezoneSelectProps {
  value: string;
  onChange: (tz: string) => void;
}

function shortenZone(tz: string): string {
  const parts = tz.split("/");
  const last = parts[parts.length - 1].replace(/_/g, " ");
  return last;
}

export function TimezoneSelect({ value, onChange }: TimezoneSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const query = search.toLowerCase().trim();
    if (!query) return US_TIMEZONE_REGIONS;
    return US_TIMEZONE_REGIONS.map((group) => ({
      ...group,
      zones: group.zones.filter(
        (z) =>
          z.toLowerCase().includes(query) ||
          shortenZone(z).toLowerCase().includes(query),
      ),
    })).filter((g) => g.zones.length > 0);
  }, [search]);

  const hasResults = filtered.some((g) => g.zones.length > 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 w-full justify-between font-normal"
          onClick={() => setOpen(true)}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value ? shortenZone(value) : "Select timezone…"}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search US timezones…"
              className="h-8 pl-8 text-xs"
            />
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto">
          {!hasResults && (
            <div className="py-6 text-center text-xs text-muted-foreground">
              No matching timezones
            </div>
          )}
          {filtered.map((group) => (
            <div key={group.label}>
              <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                {group.label}
              </div>
              {group.zones.map((tz) => (
                <button
                  key={tz}
                  type="button"
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-1.5 text-xs hover:bg-hover/50 transition-colors",
                    value === tz && "bg-primary/5 text-primary",
                  )}
                  onClick={() => {
                    onChange(tz);
                    setSearch("");
                    setOpen(false);
                  }}
                >
                  <span>{shortenZone(tz)}</span>
                  {value === tz && <Check className="h-3.5 w-3.5" />}
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
